import { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  Clock,
  CheckCircle,
  TrendingUp,
  Wallet,
  Users,
  Search,
  Filter,
  X,
  Copy,
  Check,
} from 'lucide-react';
import { campaignService } from '../services/campaignService';
import { InfluencerPaymentCard } from './InfluencerPaymentCard';
import type { CampaignWithComputed } from '../types/campaign';
import type {
  PaymentCategory,
  InfluencerPaymentSummary,
  PaymentStats,
} from '../types/payment';
import {
  PAYMENT_CATEGORIES,
  groupCampaignsByInfluencer,
  calculatePaymentStats,
  getPaymentCategoryAmount,
} from '../types/payment';
import { formatEuro } from '../utils/currency';
import { getParcelLabel } from '../utils/payableReadiness';
import { getCampaignReferenceDate } from '../utils/dates';
import { getInvoiceStats } from '../utils/paymentCalculations';
import { useModal } from '../contexts/ModalContext';
import { log } from '../lib/logger';

interface PaymentsPageProps {
  onCampaignClick?: (campaign: CampaignWithComputed) => void;
}

interface StatCardProps {
  label: string;
  count: number;
  amount: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  isActive: boolean;
  onClick: () => void;
}

function StatCard({
  label,
  count,
  amount,
  icon,
  color,
  bgColor,
  borderColor,
  isActive,
  onClick,
}: StatCardProps) {
  return (
    <button
      onClick={onClick}
      className={`${bgColor} ${borderColor} border-2 rounded-xl p-4 text-left transition-all hover:shadow-md ${
        isActive ? 'ring-2 ring-offset-2 ring-blue-500' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${color} bg-white/50`}>{icon}</div>
        <span
          className={`text-2xl font-bold ${color.replace('text-', 'text-')}`}
        >
          {count}
        </span>
      </div>
      <div className={`text-sm font-medium ${color}`}>{label}</div>
      <div className={`text-lg font-bold ${color} mt-1`}>
        {formatEuro(amount)}
      </div>
    </button>
  );
}

export function PaymentsPage({ onCampaignClick }: PaymentsPageProps) {
  const [campaigns, setCampaigns] = useState<CampaignWithComputed[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] =
    useState<PaymentCategory | null>(null);
  const [copied, setCopied] = useState(false);
  const { toast } = useModal();

  useEffect(() => {
    loadCampaigns();
  }, []);

  const handleMarkAsPaid = async (campaignId: string, amount: number, creatorName?: string) => {
    try {
      await campaignService.markInfluencerAsPaid(campaignId, amount, creatorName);
      toast.success('Payment recorded successfully');
      await loadCampaigns();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to record payment';
      toast.error(message);
      throw err;
    }
  };

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await campaignService.getAll();
      setCampaigns(data);
    } catch (error) {
      log.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const influencerSummaries = useMemo(
    () => groupCampaignsByInfluencer(campaigns),
    [campaigns]
  );

  const stats = useMemo(
    () => calculatePaymentStats(influencerSummaries),
    [influencerSummaries]
  );

  const filteredSummaries = useMemo(() => {
    let result = influencerSummaries;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((s) => s.influencer.toLowerCase().includes(term));
    }

    if (selectedCategory) {
      result = result.filter((s) => s.counts[selectedCategory] > 0);
    }

    return result;
  }, [influencerSummaries, searchTerm, selectedCategory]);

  const toggleCategory = (category: PaymentCategory) => {
    setSelectedCategory((prev) => (prev === category ? null : category));
  };

  const getMonthOfDeliverable = (campaign: CampaignWithComputed): string => {
    const referenceDate = getCampaignReferenceDate(campaign as { delivery_date?: string | null; event_date?: string | null });
    if (!referenceDate) return 'N/A';
    return new Date(referenceDate).toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'short',
    });
  };

  const copyFilteredPayments = async () => {
    if (!selectedCategory || filteredSummaries.length === 0) return;

    const padRight = (str: string, length: number) =>
      str + ' '.repeat(Math.max(0, length - str.length));

    const workCodeWidth = 15;
    const campaignRefWidth = 20;
    const brandWidth = 25;
    const monthWidth = 20;
    const netWidth = 15;
    const influencerWidth = 25;
    const parcelWidth = selectedCategory === 'ready' ? 10 : 0;

    const header = selectedCategory === 'ready'
      ? `${padRight('Influencer', influencerWidth)}${padRight('Work Code', workCodeWidth)}${padRight('Campaign Ref', campaignRefWidth)}${padRight('Brand', brandWidth)}${padRight('Month', monthWidth)}${padRight('Amount Available', netWidth)}${padRight('Parcel', parcelWidth)}`
      : `${padRight('Influencer', influencerWidth)}${padRight('Work Code', workCodeWidth)}${padRight('Campaign Ref', campaignRefWidth)}${padRight('Brand', brandWidth)}${padRight('Month', monthWidth)}${padRight('Net Influencer', netWidth)}`;
    const separator = '-'.repeat(
      influencerWidth + workCodeWidth + campaignRefWidth + brandWidth + monthWidth + netWidth + parcelWidth
    );

    let allRows: string[] = [];
    let grandTotal = 0;

    filteredSummaries.forEach((summary) => {
      const campaignsForCategory = summary.campaigns[selectedCategory];
      campaignsForCategory.forEach((campaign) => {
        const influencer = padRight(summary.influencer.substring(0, 24), influencerWidth);
        const workCode = padRight(campaign.work_code || 'N/A', workCodeWidth);
        const campaignRef = padRight(
          (campaign.campaign_ref || 'N/A').substring(0, 19),
          campaignRefWidth
        );
        const brand = padRight(
          (campaign.brand || campaign.client || 'N/A').substring(0, 24),
          brandWidth
        );
        const month = padRight(getMonthOfDeliverable(campaign), monthWidth);

        if (selectedCategory === 'ready') {
          const invoiceStats = getInvoiceStats(campaign);
          const net = padRight(formatEuro(invoiceStats.netPayable), netWidth);
          const parcel = padRight(getParcelLabel(campaign, invoiceStats), parcelWidth);
          allRows.push(`${influencer}${workCode}${campaignRef}${brand}${month}${net}${parcel}`);
          grandTotal += invoiceStats.netPayable;
        } else {
          const amount = getPaymentCategoryAmount(campaign, selectedCategory);
          const net = padRight(formatEuro(amount), netWidth);
          allRows.push(`${influencer}${workCode}${campaignRef}${brand}${month}${net}`);
          grandTotal += amount;
        }
      });
    });

    const totalLabel = padRight(
      'Total:',
      influencerWidth + workCodeWidth + campaignRefWidth + brandWidth + monthWidth
    );
    const message = `${header}\n${separator}\n${allRows.join('\n')}\n${separator}\n${totalLabel}${formatEuro(grandTotal)}`;

    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      log.error('Failed to copy:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-12 text-center">
        <RefreshCw className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
        <p className="text-gray-500 text-lg">A carregar pagamentos…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label={PAYMENT_CATEGORIES.active.label}
          count={stats.countActive}
          amount={stats.totalActive}
          icon={<Clock className="w-5 h-5" />}
          color="text-blue-700"
          bgColor="bg-blue-50"
          borderColor="border-blue-200"
          isActive={selectedCategory === 'active'}
          onClick={() => toggleCategory('active')}
        />
        <StatCard
          label={PAYMENT_CATEGORIES.ready.label}
          count={stats.countReady}
          amount={stats.totalReady}
          icon={<CheckCircle className="w-5 h-5" />}
          color="text-green-700"
          bgColor="bg-green-50"
          borderColor="border-green-200"
          isActive={selectedCategory === 'ready'}
          onClick={() => toggleCategory('ready')}
        />
        <StatCard
          label={PAYMENT_CATEGORIES.forecast.label}
          count={stats.countForecast}
          amount={stats.totalForecast}
          icon={<TrendingUp className="w-5 h-5" />}
          color="text-amber-700"
          bgColor="bg-amber-50"
          borderColor="border-amber-200"
          isActive={selectedCategory === 'forecast'}
          onClick={() => toggleCategory('forecast')}
        />
        <StatCard
          label={PAYMENT_CATEGORIES.paid.label}
          count={stats.countPaid}
          amount={stats.totalPaid}
          icon={<Wallet className="w-5 h-5" />}
          color="text-slate-600"
          bgColor="bg-slate-50"
          borderColor="border-slate-200"
          isActive={selectedCategory === 'paid'}
          onClick={() => toggleCategory('paid')}
        />
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-xl p-4 text-left">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 rounded-lg bg-white/10 text-white">
              <Users className="w-5 h-5" />
            </div>
            <span className="text-2xl font-bold text-white">
              {stats.influencerCount}
            </span>
          </div>
          <div className="text-sm font-medium text-slate-300">Criadores</div>
          <div className="text-lg font-bold text-white mt-1">
            {formatEuro(
              stats.totalActive +
                stats.totalReady +
                stats.totalForecast +
                stats.totalPaid
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Procurar criadores…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          {selectedCategory && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Filter className="w-4 h-4" />
                {PAYMENT_CATEGORIES[selectedCategory].label}
                <X className="w-4 h-4" />
              </button>
              <button
                onClick={copyFilteredPayments}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  copied
                    ? 'bg-brand-ink text-white'
                    : 'bg-brand-lime text-brand-ink hover:bg-brand-lime-hover'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copiar tudo
                  </>
                )}
              </button>
            </div>
          )}
          <button
            onClick={loadCampaigns}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {filteredSummaries.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-12 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">
            {searchTerm || selectedCategory
              ? 'Nenhum criador corresponde aos filtros'
              : 'Sem pagamentos para mostrar'}
          </p>
          {(searchTerm || selectedCategory) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory(null);
              }}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Limpar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-gray-500">
            A mostrar {filteredSummaries.length} de {influencerSummaries.length}{' '}
            {influencerSummaries.length !== 1 ? 'criadores' : 'criador'}
          </div>
          {filteredSummaries.map((summary) => (
            <InfluencerPaymentCard
              key={summary.influencer}
              summary={summary}
              onCampaignClick={onCampaignClick}
              onMarkAsPaid={handleMarkAsPaid}
            />
          ))}
        </div>
      )}
    </div>
  );
}
